import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { OfferPreviewCardContainer } from './OfferPreviewCardContainer';
import { OFFER_PREVIEW_SAFE_AREA } from './constants';
import { useLocalPreviewOffer } from './hooks/useLocalPreviewOffer';
import * as styles from './styles.css';
import { isCompactDesktopPreview } from './utils/isCompactDesktopPreview';
import { closeOfferPreview } from '../../../common/actions/closeOfferPreview/closeOfferPreview';
import { OfferPreviewBalloon } from '../../../common/components/OfferCardPreview/components/OfferPreviewBalloon';
import { OfferPreviewError } from '../../../common/components/OfferCardPreview/components/OfferPreviewError';
import { OfferPreviewLoading } from '../../../common/components/OfferCardPreview/components/OfferPreviewLoading';
import { OfferPreviewModal } from '../../../common/components/OfferCardPreview/components/OfferPreviewModal';
import { getDesktopPreviewSize } from '../../../common/components/OfferCardPreview/utils/getDesktopPreviewSize';
import { getPinAnchor } from '../../../common/constants/clusterPins/getPinAnchor';
import { IDailyRentOfferForMobileSite } from '../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectIsOffersLoading, selectListingOffers } from '../../../common/selectors/listingOffers';
import { TThunkDispatch } from '../../../common/types/redux';
import { selectActiveFeature } from '../../selectors';
import { useMaxPinsPerCluster } from '../Map/hooks/useMaxPinsPerCluster';

export interface IOfferPreviewContainerProps {
  listingSafeAreaWidth: number;
  map: YMaps.Map;
  ymaps: YMaps.IYMaps;
}

function getPreviewOffer(offers: IDailyRentOfferForMobileSite[], cianId: number | undefined) {
  if (!cianId) {
    return undefined;
  }

  return offers.find(offer => offer.id === cianId || offer.cianId === cianId);
}

export const OfferPreviewContainer = ({ listingSafeAreaWidth, ymaps, map }: IOfferPreviewContainerProps) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const activeFeature = useSelector(selectActiveFeature);
  const isLoading = useSelector(selectIsOffersLoading);
  const offers = useSelector(selectListingOffers);
  const cianId = activeFeature?.properties.offers?.[0]?.cianId;
  const listingOffer = React.useMemo(() => getPreviewOffer(offers, cianId), [offers, cianId]);
  const { isPreviewOfferLoading, offer, retryPreviewOffer } = useLocalPreviewOffer({
    cianId,
    isListingOffersLoading: isLoading,
    listingOffer,
  });
  const isPhone = deviceType === 'phone';

  const onClose = React.useCallback(() => {
    dispatch(closeOfferPreview());
  }, [dispatch]);

  const safeArea = React.useMemo(
    () => ({ ...OFFER_PREVIEW_SAFE_AREA, left: listingSafeAreaWidth }),
    [listingSafeAreaWidth],
  );

  // Режим превью фиксируется на момент открытия: activeFeature в deps - ключ открытия (смена пина),
  // хотя isCompactDesktopPreview его не читает (берёт текущий matchMedia). Resize открытого не меняет.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isCompactPreview = React.useMemo(() => isCompactDesktopPreview(), [activeFeature]);
  const isCompact = !isPhone && isCompactPreview;
  const { width: previewWidth, height: previewHeight } = getDesktopPreviewSize(isCompactPreview);

  if (!activeFeature) {
    return null;
  }

  const content = (() => {
    if (!offer) {
      if (isPreviewOfferLoading) {
        return <OfferPreviewLoading className={!isPhone ? styles['card_desktop'] : undefined} isCompact={isCompact} />;
      }

      return (
        <OfferPreviewError
          className={!isPhone ? styles['card_desktop'] : undefined}
          isCompact={isCompact}
          onClose={onClose}
          onRetry={retryPreviewOffer}
        />
      );
    }

    return (
      <OfferPreviewCardContainer
        className={!isPhone ? styles['card_desktop'] : undefined}
        hasCloseControl={isPhone}
        isCompact={isCompact}
        showButtons
        narrowButtons={!isPhone}
        offer={offer}
      />
    );
  })();

  if (isPhone) {
    return (
      <OfferPreviewModal isOpen={Boolean(activeFeature)} onClose={onClose}>
        {content}
      </OfferPreviewModal>
    );
  }

  const anchor = getPinAnchor(activeFeature.properties, maxPinsPerCluster);

  return (
    <OfferPreviewBalloon
      activeFeature={activeFeature}
      anchor={anchor}
      ymaps={ymaps}
      map={map}
      onClose={onClose}
      previewHeight={previewHeight}
      previewWidth={previewWidth}
      safeArea={safeArea}
    >
      {content}
    </OfferPreviewBalloon>
  );
};

OfferPreviewContainer.displayName = 'OfferPreviewContainer';
